import {
  CalendarDate,
  CalendarDateTime,
  getLocalTimeZone,
  startOfWeek,
  today,
} from "@internationalized/date";
import { RangeValue } from "@react-types/shared";
import { format } from "date-fns";
import { DateTime } from "luxon";

/** Anything more recent than this reads as "just now" rather than "N seconds ago". */
const JUST_NOW_THRESHOLD_SECONDS = 45;

export const fromNow = (date: string | Date) => {
  const dt =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);
  if (!dt.isValid) return "";
  if (Math.abs(DateTime.now().diff(dt, "seconds").seconds) < JUST_NOW_THRESHOLD_SECONDS) {
    return "just now";
  }
  return dt.toRelative() ?? "";
};

/**
 * Shows relative time up to 3 days ("1 day ago", "2 days ago", "3 days ago"),
 * then switches to absolute date in dd/MM/yyyy format.
 */
export const fromNowShort = (date: string | Date) => {
  const dt =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);
  if (!dt.isValid) return "";
  const now = DateTime.now();
  const diffDays = Math.abs(now.diff(dt, "days").days);

  if (diffDays <= 3) {
    if (Math.abs(now.diff(dt, "seconds").seconds) < JUST_NOW_THRESHOLD_SECONDS) {
      return "just now";
    }
    return dt.toRelative() ?? "";
  }

  return dt.toFormat("dd/MM/yyyy");
};

export const formatDate = (date: string | Date): string => {
  const input =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);
  const now = DateTime.now();
  const todayStart = now.startOf("day");
  const yesterdayStart = now.minus({ days: 1 }).startOf("day");
  const twoDaysAgoStart = now.minus({ days: 2 }).startOf("day");

  if (input.hasSame(todayStart, "day")) {
    return "Today";
  }

  if (input.hasSame(yesterdayStart, "day")) {
    return "Yesterday";
  }

  if (input.hasSame(twoDaysAgoStart, "day")) {
    return "2 days ago";
  }

  if (input.hasSame(now, "year")) {
    return input.toFormat("MMM d");
  }

  return input.toFormat("MMM d, yyyy");
};

export const formatChartDate = (date: string | Date): string => {
  const input =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);
  return input.toFormat("MMM d");
};

export type Granularity =
  | "5min"
  | "2hour"
  | "hourly"
  | "daily"
  | "weekly"
  | "monthly";

export const getGranularity = (
  startDate: string | Date,
  endDate: string | Date,
): Granularity => {
  const start = typeof startDate === "string" ? new Date(startDate) : startDate;
  const end = typeof endDate === "string" ? new Date(endDate) : endDate;

  const durationMs = end.getTime() - start.getTime();
  const minutes = durationMs / (1000 * 60);
  const hours = minutes / 60;
  const days = hours / 24;

  if (days >= 365) return "monthly";
  if (days >= 28) return "weekly";
  if (days >= 2) return "daily";

  const isSameDay = start.toDateString() === end.toDateString();
  if (isSameDay || hours >= 24) return "hourly";
  if (minutes >= 60) return "2hour";
  return "5min";
};

export const formatChartTimestamp = (
  timestamp: string | Date,
  granularity: Granularity,
): string => {
  const dt =
    typeof timestamp === "string"
      ? DateTime.fromISO(timestamp)
      : DateTime.fromJSDate(timestamp);

  switch (granularity) {
    case "5min":
    case "2hour":
    case "hourly":
      return dt.toFormat("h:mm a");
    case "daily":
      return dt.toFormat("MMM d");
    case "weekly":
      return dt.toFormat("MMM d");
    case "monthly":
      return dt.toFormat("MMM yyyy");
  }
};

export const formatTime = (date: string | Date): string => {
  const dt =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);
  return dt.toFormat("hh:mm a");
};

export const formatDateTime = (date: string | Date): string => {
  const formattedDate = formatDate(date);
  const formattedTime = formatTime(date);

  return `${formattedTime}, ${formattedDate}`;
};

export const formatDateTimeRange = (
  dateStart: string | Date,
  dateEnd: string | Date,
): string => {
  const dateStartAsDate =
    typeof dateStart === "string" ? new Date(dateStart) : dateStart;
  const dateEndAsDate =
    typeof dateEnd === "string" ? new Date(dateEnd) : dateEnd;
  const formattedDate = dateStartAsDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const formattedTimeRange =
    format(dateStartAsDate, "h:mm a") + " - " + format(dateEndAsDate, "h:mm a");

  return `${formattedDate}, ${formattedTimeRange}`;
};

export const isWithin24Hours = (timestamp: string | Date): boolean => {
  const dt =
    typeof timestamp === "string"
      ? DateTime.fromISO(timestamp)
      : DateTime.fromJSDate(timestamp);
  const hoursDiff = DateTime.now().diff(dt, "hours").hours;
  return hoursDiff < 24;
};

export const isWithin7Days = (timestamp: string | Date): boolean => {
  const dt =
    typeof timestamp === "string"
      ? DateTime.fromISO(timestamp)
      : DateTime.fromJSDate(timestamp);
  const daysDiff = DateTime.now().diff(dt, "days").days;
  return daysDiff < 7;
};

export const todayDate = () => {
  return DateTime.now().toFormat("yyyy-MM-dd");
};

export const oneWeekFromNowDate = () => {
  return DateTime.now().plus({ days: 7 }).toFormat("yyyy-MM-dd");
};

export const oneWeekBeforeDate = () => {
  return DateTime.now().minus({ days: 7 }).toFormat("yyyy-MM-dd");
};

export const isBefore = (date1: string, date2: string) => {
  return DateTime.fromISO(date1) < DateTime.fromISO(date2);
};

export const isAfter = (date1: string, date2: string) => {
  return DateTime.fromISO(date1) > DateTime.fromISO(date2);
};

export const isSameOrBefore = (date1: string | Date, date2: string | Date) => {
  const dt1 =
    typeof date1 === "string"
      ? DateTime.fromISO(date1)
      : DateTime.fromJSDate(date1);
  const dt2 =
    typeof date2 === "string"
      ? DateTime.fromISO(date2)
      : DateTime.fromJSDate(date2);
  return dt1 <= dt2;
};

export const USER_TIMEZONE = DateTime.local().zoneName ?? "UTC";

/**
 * Returns the default date range for reports (start of current week to today)
 */
export const getDefaultDateRange = (): RangeValue<CalendarDateTime> => {
  const tz = getLocalTimeZone();
  const todayDate = today(tz);
  const weekStart = startOfWeek(todayDate, "en-US");

  return {
    start: new CalendarDateTime(
      weekStart.year,
      weekStart.month,
      weekStart.day,
      0,
      0,
      0,
    ),
    end: new CalendarDateTime(
      todayDate.year,
      todayDate.month,
      todayDate.day,
      23,
      59,
      59,
    ),
  };
};

/**
 * Returns the nearest time option from a list of time strings (format: "hh:mm AM/PM")
 * based on the current time
 */
export const getNearestTimeOption = (timeOptions: string[]): string => {
  const now = DateTime.now();
  const currentMinutes = now.hour * 60 + now.minute;

  let nearestTime = timeOptions[0];
  let minDiff = Infinity;

  for (const time of timeOptions) {
    const [timeStr, period] = time.split(" ");
    const [hourStr, minuteStr] = timeStr.split(":");
    let hour = parseInt(hourStr);
    const minute = parseInt(minuteStr);

    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;

    const optionMinutes = hour * 60 + minute;
    const diff = Math.abs(optionMinutes - currentMinutes);

    if (diff < minDiff) {
      minDiff = diff;
      nearestTime = time;
    }
  }

  return nearestTime;
};

/**
 * Generate time options with specified interval (default: 15 minutes)
 * Returns array of time strings in "hh:mm a" format (e.g., "09:00 AM")
 */
export const generateTimeOptions = (interval: number = 15): string[] => {
  const times: string[] = [];
  let current = DateTime.now().startOf("day");
  const end = DateTime.now().endOf("day");

  while (current < end) {
    times.push(current.toFormat("hh:mm a"));
    current = current.plus({ minutes: interval });
  }

  return times;
};

/**
 * Format hour/minute to time string (e.g., "09:00 AM")
 */
export const formatTimeValue = (hour: number, minute: number): string => {
  return DateTime.now().set({ hour, minute }).toFormat("hh:mm a");
};

/**
 * Format hour/minute to 24-hour wire format string (e.g., "09:05")
 */
export const format24HourTime = (hour: number, minute: number): string =>
  `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

/**
 * Parse 24-hour wire format string (e.g., "09:05") to hour/minute.
 * Defaults to 0 for any malformed segment.
 */
export const parseTime24Hour = (
  value: string,
): { hour: number; minute: number } => {
  const [h, m] = value.split(":").map(Number);
  return { hour: h ?? 0, minute: m ?? 0 };
};

/**
 * Parse time string (e.g., "09:00 AM") to hour/minute in 24-hour format
 */
export const parseTimeString = (
  timeStr: string,
): { hour: number; minute: number } | null => {
  const [time, period] = timeStr.split(" ");
  if (!time || !period) return null;

  const [hourStr, minuteStr] = time.split(":");
  let hour = parseInt(hourStr);
  const minute = parseInt(minuteStr);

  if (isNaN(hour) || isNaN(minute)) return null;

  // Convert to 24-hour format
  if (period === "PM" && hour !== 12) hour += 12;
  if (period === "AM" && hour === 12) hour = 0;

  return { hour, minute };
};

/**
 * Convert ISO string to CalendarDateTime
 */
export const isoToCalendarDateTime = (
  isoString: string | null | undefined,
): CalendarDateTime | null => {
  if (!isoString) return null;

  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return null;

    return new CalendarDateTime(
      date.getFullYear(),
      date.getMonth() + 1, // CalendarDateTime months are 1-indexed
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    );
  } catch {
    return null;
  }
};

export const isoToCalendarDate = (
  isoString: string | null | undefined,
): CalendarDate | null => {
  if (!isoString) return null;

  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return null;

    return new CalendarDate(
      date.getFullYear(),
      date.getMonth() + 1, // CalendarDateTime months are 1-indexed
      date.getDate(),
    );
  } catch {
    return null;
  }
};

/**
 * Convert local time string ("hh:mm a") to UTC time string ("HH:mm")
 */
export const localTimeToUtc = (timeStr: string): string => {
  const parsed = parseTimeString(timeStr);
  if (!parsed) return timeStr;

  const local = DateTime.now().set({
    hour: parsed.hour,
    minute: parsed.minute,
    second: 0,
    millisecond: 0,
  });

  const utc = local.toUTC();

  return utc.toFormat("HH:mm");
};

/**
 * Convert UTC time string ("HH:mm") to local time string ("hh:mm a")
 */
/**
 * Format a duration in minutes to a human-readable string.
 * e.g. 12 → "12m", 145 → "2h 25m", 1500 → "1d 1h"
 */
export const formatDurationMinutes = (minutes: number): string => {
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${Math.round(minutes)}m`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);

  if (hours < 24) {
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
};

export const utcToLocalTime = (utcTimeStr: string): string => {
  const [hourStr, minuteStr] = utcTimeStr.split(":");
  const hour = parseInt(hourStr);
  const minute = parseInt(minuteStr);

  if (isNaN(hour) || isNaN(minute)) return utcTimeStr;

  const utc = DateTime.utc().set({
    hour,
    minute,
    second: 0,
    millisecond: 0,
  });

  const local = utc.toLocal();

  return local.toFormat("hh:mm a");
};

/**
 * Convert CalendarDateTime to ISO string
 */
export const calendarDateTimeToIso = (
  dateTime: CalendarDateTime | null,
): string | null => {
  if (!dateTime) return null;

  const date = new Date(
    dateTime.year,
    dateTime.month - 1, // JS Date months are 0-indexed
    dateTime.day,
    dateTime.hour,
    dateTime.minute,
    dateTime.second || 0,
  );
  return date.toISOString();
};

/**
 * Structural shape for any @internationalized/date value. Used to bridge the
 * version skew between our pinned @internationalized/date and the copy nested
 * under react-stately/react-aria, whose CalendarDateTime is structurally
 * distinct under TypeScript.
 */
export interface DateValueLike {
  year: number;
  month: number;
  day: number;
  hour?: number;
  minute?: number;
  second?: number;
}

/**
 * Coerce a DateValue-shaped object (from any @internationalized/date copy)
 * into our local CalendarDateTime instance.
 */
export const toLocalCalendarDateTime = (
  date: DateValueLike | null,
): CalendarDateTime | null => {
  if (!date) return null;
  return new CalendarDateTime(
    date.year,
    date.month,
    date.day,
    date.hour ?? 0,
    date.minute ?? 0,
    date.second ?? 0,
  );
};

export const formatCalendarDateTime = (
  dateTime: CalendarDateTime,
  format?: string,
): string => {
  return DateTime.fromObject({
    year: dateTime.year,
    month: dateTime.month,
    day: dateTime.day,
    hour: dateTime.hour,
    minute: dateTime.minute,
  }).toFormat(format ?? "MMM dd, yyyy hh:mm a");
};

export const formatCalendarTime = (dateTime: CalendarDateTime): string => {
  return DateTime.now()
    .set({ hour: dateTime.hour, minute: dateTime.minute })
    .toFormat("hh:mm a");
};

const isFullDayCalendarRange = (
  start: CalendarDateTime,
  end: CalendarDateTime,
): boolean =>
  start.hour === 0 &&
  start.minute === 0 &&
  end.hour === 23 &&
  (end.minute === 59 || end.minute === 0);

// Format CalendarDateTime range for display.
// - Drops the year segment when start and end share the same year.
// - Drops the time segment when the range spans a full day (00:00 → 23:59).
export const formatCalendarDateTimeRange = (
  start: CalendarDateTime,
  end: CalendarDateTime,
): string => {
  try {
    const startLuxon = DateTime.fromObject({
      year: start.year,
      month: start.month,
      day: start.day,
      hour: start.hour,
      minute: start.minute,
    });
    const endLuxon = DateTime.fromObject({
      year: end.year,
      month: end.month,
      day: end.day,
      hour: end.hour,
      minute: end.minute,
    });

    const sameYear = start.year === end.year;
    const fullDay = isFullDayCalendarRange(start, end);

    const datePart = sameYear ? "MMM dd" : "MMM dd, yyyy";
    const format = fullDay ? datePart : `${datePart} hh:mm a`;

    return `${startLuxon.toFormat(format)} - ${endLuxon.toFormat(format)}`;
  } catch (e) {
    console.error("Error formatting date range:", start, end, e);

    return "Invalid date range";
  }
};

/**
 * Get current CalendarDateTime (now)
 */
export const getCurrentCalendarDateTime = (): CalendarDateTime => {
  const now = DateTime.now();
  return new CalendarDateTime(
    now.year,
    now.month,
    now.day,
    now.hour,
    now.minute,
    now.second,
  );
};

/**
 * Compact elapsed-time label for an overdue moment, e.g. "45m", "3h 20m", "18d 5h".
 * Days and hours derive from the same elapsed minutes so the parts stay consistent.
 */
export const formatOverdueDuration = (overdueMinutes: number): string => {
  if (overdueMinutes < 60) return `${overdueMinutes}m`;

  const totalHours = Math.floor(overdueMinutes / 60);

  if (totalHours < 24) {
    const minutes = overdueMinutes % 60;
    return minutes > 0 ? `${totalHours}h ${minutes}m` : `${totalHours}h`;
  }

  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
};

export const formatFollowupDate = (dueDate: string): string => {
  const dt = DateTime.fromISO(dueDate).toLocal();
  const now = DateTime.now();

  const diffDays = Math.round(
    dt.startOf("day").diff(now.startOf("day"), "days").days,
  );

  const time = dt.toFormat("h:mm a");

  if (diffDays < 0) {
    const overdueMinutes = Math.floor(now.diff(dt, "minutes").minutes);
    return formatOverdueDuration(overdueMinutes);
  }

  if (diffDays === 0) {
    const diffMinutes = Math.floor(dt.diff(now, "minutes").minutes);

    if (diffMinutes <= 0) return "Due now";

    if (diffMinutes < 60) {
      return `In ${diffMinutes}m`;
    }

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    return minutes > 0 ? `In ${hours}h ${minutes}m` : `In ${hours}h`;
  }

  if (diffDays === 1) return `Tomorrow, ${time}`;
  if (diffDays <= 7) return `In ${diffDays}d, ${time}`;

  return dt.toFormat("MMM dd, h:mm a");
};

export const isOverdue = (dueDate: string): boolean => {
  return DateTime.fromISO(dueDate).toLocal() < DateTime.now();
};

export const isDueToday = (dueDate: string): boolean => {
  return DateTime.fromISO(dueDate)
    .toLocal()
    .startOf("day")
    .equals(DateTime.now().startOf("day"));
};
